import React, { useContext, useEffect } from 'react';
import { cloneDeep, isEmpty, omit } from 'lodash';
import { styled } from '@mui/material';

import { DrawerActions, MilestoneView } from 'components/organisms/Milestones';
import { TemplateDrawerInner } from 'components/organisms/TaskTemplates';
import { useInstructor } from 'contexts/instructorRole';
import { PageContext } from 'pages/TaskManager/PageProvider';
import { IconButton, Stack } from 'shell/components';

const MilestoneDrawerInner = ({
    isOpen = false,
    milestone = {},
    files,
    setFiles,
    sharedUrls,
    setSharedUrls,
    changes,
    setChanges,
    updateMilestone,
    deleteMilestone,
    saveMilestone,
    publishMilestone,
    unpublishMilestone,
    unscheduleMilestone,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    saveTemplate,
    publishTemplate,
    unpublishTemplate,
    unscheduleTemplate,
    setMilestoneDrawer,
    setUploadResources,
    setScheduleModal,
    setConfirmationModal,
    closeConfirmationModal,
}) => {
    const { courseGroups, taskTemplates } = useInstructor();
    const { view, setView, activeTemplateId, setActiveTemplateId, templateChanges, setTemplateChanges, spawnedTasks } =
        useContext(PageContext);

    const handleClickBack = () => {
        if (milestone.isLocal) {
            setConfirmationModal({
                visible: true,
                destructive: true,
                title: 'Leave Task Creation?',
                content: 'All changes made to this task will not be saved.',
                confirmText: 'Leave',
                onConfirm: () => {
                    closeConfirmationModal();
                    setMilestoneDrawer({ isOpen: false, milestoneId: '' });
                    deleteMilestone(milestone.id);
                },
                onCancel: closeConfirmationModal,
            });
        } else {
            if (!Boolean(Object.keys(omit(changes, ['published_on'])).length)) {
                setChanges({});
                setMilestoneDrawer({ isOpen: false, milestoneId: '' });
            } else {
                setConfirmationModal({
                    visible: true,
                    destructive: true,
                    title: 'Discard Changes?',
                    content: 'All changes made to this milestone will not be saved.',
                    confirmText: 'Leave',
                    onConfirm: () => {
                        closeConfirmationModal();
                        setMilestoneDrawer({ isOpen: false, milestoneId: '' });
                        setChanges({});
                    },
                    onCancel: closeConfirmationModal,
                });
            }
        }
    };

    const handleMilestoneChange = (key, value) => {
        if (milestone.isLocal) {
            const newMilestone = cloneDeep(milestone);
            newMilestone.modified_on = new Date().getTime();
            newMilestone[key] = value;
            updateMilestone(milestone.id, newMilestone);
        } else {
            setChanges((current) => ({ ...current, [key]: value }));
        }
    };

    const handleAddResources = () => {
        setUploadResources((current) => ({ ...current, isOpen: true }));
    };

    const handleDeleteResources = (resourceType, resourceId) => {
        switch (resourceType) {
            case 'file':
                setFiles((current) => ({
                    ...current,
                    [resourceId]: { ...current[resourceId], active: false },
                }));
                break;
            case 'link':
                setSharedUrls((current) => ({
                    ...current,
                    [resourceId]: { ...current[resourceId], active: false },
                }));
                break;
            default:
                break;
        }

        if (milestone.isLocal) {
            const resources = milestone.resources.filter((resource) => resource.id !== resourceId);
            updateMilestone(milestone.id, { ...milestone, resources });
        } else {
            setChanges((current) => {
                const resource = (current.resources || []).find((resource) => resource.id === resourceId);
                if (resource) {
                    return {
                        ...current,
                        resources:
                            resource.action === 'add'
                                ? current.resources.filter((resource) => resource.id !== resourceId)
                                : current.resources.map((resource) => {
                                      return resource.id === resourceId ? { ...resource, action: 'delete' } : resource;
                                  }),
                    };
                } else {
                    const targetResource = [...Object.values(files), ...Object.values(sharedUrls)].find(
                        (resource) => resource.id === resourceId
                    );
                    return {
                        ...current,
                        resources: [
                            ...(current.resources || []),
                            {
                                ...targetResource,
                                action: 'delete',
                                type: targetResource.data ? 'file' : 'link',
                            },
                        ],
                    };
                }
            });
        }
    };

    const handleClickTemplate = (templateId) => {
        setView('template');
        setActiveTemplateId(templateId);
    };

    const handleUnlinkTemplate = (templateId) => {
        updateTemplate(templateId, { milestone_id: '' });
    };

    const handleTemplateDrawer = ({ isOpen, templateId }) => {
        if (!isOpen && isEmpty(templateChanges)) {
            setView('milestone');
            setActiveTemplateId(templateId);
        }
    };

    const { published_on } = milestone;
    const milestoneState = !published_on ? 'draft' : published_on < new Date().getTime() ? 'published' : 'scheduled';

    useEffect(() => {
        if (!isOpen) {
            setTimeout(() => {
                setView('milestone');
                setActiveTemplateId('');
            }, 100);
        }
    }, [isOpen]);

    return view === 'milestone' ? (
        <>
            <Stack spacing={2}>
                <IconButton
                    aria-label={'close'}
                    kind={'arrowLeft'}
                    size={'medium'}
                    variant={'square'}
                    onClick={handleClickBack}
                />
                <MilestoneView
                    milestone={milestone}
                    milestoneState={milestoneState}
                    files={files}
                    sharedUrls={sharedUrls}
                    changes={changes}
                    updateMilestone={handleMilestoneChange}
                    onAddResources={handleAddResources}
                    onDeleteResource={handleDeleteResources}
                    onClickTemplate={handleClickTemplate}
                    onUnlinkTemplate={handleUnlinkTemplate}
                    onCreateTemplate={createTemplate}
                />
            </Stack>
            <ControlsStack direction={'row'} justifyContent={'space-between'}>
                <DrawerActions
                    milestone={milestone}
                    milestoneState={milestoneState}
                    changes={changes}
                    setFiles={setFiles}
                    setSharedUrls={setSharedUrls}
                    setChanges={setChanges}
                    setView={setView}
                    setActiveTemplateId={setActiveTemplateId}
                    setScheduleModal={setScheduleModal}
                    save={saveMilestone}
                    publish={publishMilestone}
                    unpublish={unpublishMilestone}
                    unschedule={unscheduleMilestone}
                    deleteMilestone={deleteMilestone}
                    setMilestoneDrawer={setMilestoneDrawer}
                    setConfirmationModal={setConfirmationModal}
                    closeConfirmationModal={closeConfirmationModal}
                />
            </ControlsStack>
        </>
    ) : (
        <TemplateDrawerInner
            isRenderedInMilestone
            isOpen={view === 'template'}
            template={taskTemplates.find(({ id }) => id === activeTemplateId)}
            files={files}
            sharedUrls={sharedUrls}
            changes={templateChanges}
            groups={courseGroups}
            spawnedTasks={spawnedTasks}
            setFiles={setFiles}
            setSharedUrls={setSharedUrls}
            updateTemplate={updateTemplate}
            setChanges={setTemplateChanges}
            setDrawerState={handleTemplateDrawer}
            setUploadResources={setUploadResources}
            setConfirmationModal={setConfirmationModal}
            closeConfirmationModal={closeConfirmationModal}
            setScheduleModal={setScheduleModal}
            save={saveTemplate}
            publish={publishTemplate}
            unpublish={unpublishTemplate}
            unschedule={unscheduleTemplate}
            deleteTemplate={deleteTemplate}
        />
    );
};

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        bottom: '0',
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        margin: theme.spacing(0, -5),
        width: '100%',
        maxHeight: '69px',
        padding: theme.spacing(4),
        backgroundColor: theme.palette.ensightful.shades[0],
    };
});

export default MilestoneDrawerInner;
